/**
 * Diagnóstico de erros do cliente — as regras puras (rodada 2B1).
 *
 * Roda idêntico no navegador e no servidor, como o resto de `@nv/core`: o
 * cliente sanitiza ANTES de guardar na fila local; o servidor sanitiza DE NOVO
 * antes de gravar — nenhum dos dois confia no outro.
 *
 * Três decisões que organizam o módulo:
 *
 *   1. **Allowlist fechada.** `ClientErrorReport` é um tipo com campos
 *      enumerados; `validarRelatorio` recusa qualquer chave fora da lista.
 *      Não existe `metadata`, não existe JSON livre.
 *   2. **Sanitização por padrão, não por confiança.** Mensagens, stacks e rotas
 *      passam por `sanitizarTexto`/`rotaGenerica`, que apagam o que se parece
 *      com token, cookie, URL assinada, e-mail, coordenada ou id privado,
 *      e cortam no tamanho máximo. É o mesmo princípio do `redact` do log da
 *      API (invariante 14), aplicado ao que a pessoa manda.
 *   3. **Erro esperado não é incidente.** `classificarHttp` diz o que gera
 *      relatório: 5xx e falha de rede COM rede; 401/403/404/409/422 e offline
 *      esperado ficam de fora — são o produto funcionando.
 *   4. **Nenhuma mensagem livre é persistida.** `Error.message` pode carregar
 *      qualquer coisa que uma tela interpolou — "Alergia severa a amendoim",
 *      um endereço, uma descrição de despesa — e regex nenhuma reconhece isso.
 *      Então a regra é estrutural: para fontes NÃO controladas
 *      (`error_boundary`, `window_error`, `unhandled_rejection`) a mensagem
 *      é descartada e do stack ficam só os frames técnicos; para fontes
 *      controladas (`http_5xx`, `network_failure`, `sync_failure`) a mensagem
 *      tem de casar com um template que NOSSO código monta
 *      (`mensagemControladaValida`). O servidor aplica a mesma regra.
 */

/* ───────────────────────────── código de diagnóstico ───────────────────────────── */

/**
 * Alfabeto sem caracteres ambíguos (0/O, 1/I/L). 6 posições × 31 símbolos ≈
 * 887 milhões de combinações: colisão é tratada no servidor (gera outro), e a
 * enumeração é inútil porque não há endpoint de consulta por código.
 */
export const DIAGNOSTIC_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const DIAGNOSTIC_CODE_LENGTH = 6;
export const DIAGNOSTIC_CODE_RE = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{6}$/;

/** Código aleatório — não carrega usuário, viagem nem instante. */
export function gerarDiagnosticCode(aleatorio: (n: number) => Uint8Array = bytesAleatorios): string {
  const bytes = aleatorio(DIAGNOSTIC_CODE_LENGTH);
  let s = '';
  for (let i = 0; i < DIAGNOSTIC_CODE_LENGTH; i += 1) {
    s += DIAGNOSTIC_CODE_ALPHABET[(bytes[i] ?? 0) % DIAGNOSTIC_CODE_ALPHABET.length];
  }
  return s;
}

function bytesAleatorios(n: number): Uint8Array {
  const b = new Uint8Array(n);
  globalThis.crypto.getRandomValues(b);
  return b;
}

/* ───────────────────────────── tipos ───────────────────────────── */

export const CLIENT_ERROR_SOURCES = [
  'error_boundary',
  'window_error',
  'unhandled_rejection',
  'http_5xx',
  'network_failure',
  'sync_failure',
] as const;
export type ClientErrorSource = (typeof CLIENT_ERROR_SOURCES)[number];

/** O payload EXATO que o servidor aceita. Nada além destas chaves. */
export interface ClientErrorReport {
  diagnosticCode: string;
  occurredAt: string;
  source: ClientErrorSource;
  clientRelease: string;
  errorName?: string;
  message?: string;
  stack?: string;
  componentStack?: string;
  route?: string;
  requestId?: string;
  httpStatus?: number;
  serverReleaseSeen?: string;
  online?: boolean;
  standalone?: boolean;
  serviceWorkerControlled?: boolean;
  userAgent?: string;
  viewportWidth?: number;
  viewportHeight?: number;
  language?: string;
  /** Viagem ativa no aparelho; o servidor só a grava se a sessão for membro. */
  tripId?: string;
}

export const CLIENT_ERROR_REPORT_KEYS: readonly (keyof ClientErrorReport)[] = [
  'diagnosticCode',
  'occurredAt',
  'source',
  'clientRelease',
  'errorName',
  'message',
  'stack',
  'componentStack',
  'route',
  'requestId',
  'httpStatus',
  'serverReleaseSeen',
  'online',
  'standalone',
  'serviceWorkerControlled',
  'userAgent',
  'viewportWidth',
  'viewportHeight',
  'language',
  'tripId',
];

export const LIMITES = {
  errorName: 80,
  message: 500,
  stack: 4000,
  componentStack: 2000,
  route: 200,
  requestId: 64,
  release: 64,
  userAgent: 400,
  language: 20,
} as const;

/* ───────────────────────────── mensagens controladas ───────────────────────────── */

/** Fontes cuja mensagem é texto de terceiros (o `Error.message` de qualquer lugar). */
export const FONTES_NAO_CONTROLADAS: readonly ClientErrorSource[] = ['error_boundary', 'window_error', 'unhandled_rejection'];

const METODO = '(?:GET|POST|PUT|PATCH|DELETE)';
const ROTA_DA_API = '/api/[A-Za-z0-9/:_.…-]{1,150}';
export const MENSAGEM_SYNC_FAILURE = 'Falha inesperada ao sincronizar';

/**
 * O que a mensagem de cada fonte controlada PODE ser. Tudo aqui é montado por
 * código nosso a partir de rota genérica + método + status — nunca de texto
 * vindo de um Error.
 */
const TEMPLATES: Record<ClientErrorSource, RegExp | null> = {
  http_5xx: new RegExp(`^${METODO} (${ROTA_DA_API}) → 5\\d\\d$`),
  network_failure: new RegExp(`^${METODO} (${ROTA_DA_API}): falha de rede$`),
  sync_failure: new RegExp(`^${MENSAGEM_SYNC_FAILURE}$`),
  error_boundary: null,
  window_error: null,
  unhandled_rejection: null,
};

export function mensagemControladaValida(source: ClientErrorSource, message: string): boolean {
  const re = TEMPLATES[source];
  if (!re) return false;
  const m = re.exec(message);
  if (!m) return false;
  // A rota dentro do template tem de já ser genérica: um id cru no caminho
  // (uuid, data, código) não passa por parecer um template.
  const rota = m[1];
  return rota === undefined || rotaGenerica(rota) === rota;
}

/**
 * A mensagem que o relatório pode carregar: para fonte não controlada, nada;
 * para fonte controlada, só se casar com o template. Idêntico nos dois lados.
 */
export function mensagemPersistivel(source: ClientErrorSource, message: unknown): string | undefined {
  if (typeof message !== 'string') return undefined;
  return mensagemControladaValida(source, message) ? message : undefined;
}

/**
 * Classes de erro que o relatório pode nomear — uma allowlist FIXA. `error.name`
 * e `error.code` são propriedades graváveis (`e.name = 'Diabetes'`), e um
 * objeto rejeitado pode ter `name` com qualquer coisa: por isso nenhum deles é
 * lido. O que vale é a CLASSE (`constructor.name`) de um `Error` de verdade,
 * e só se estiver nesta lista; fora dela vira `Error`. Valores que não são
 * `Error` recebem uma categoria fixa pelo tipo (`Rejeicao_Object`…), sem olhar
 * o conteúdo.
 */
export const CLASSES_DE_ERRO = [
  'Error',
  'TypeError',
  'RangeError',
  'SyntaxError',
  'ReferenceError',
  'EvalError',
  'URIError',
  'AggregateError',
  'DOMException',
  'Rejeicao_Object',
  'Rejeicao_Array',
  'Rejeicao_String',
  'Rejeicao_Number',
  'Rejeicao_Boolean',
  'Rejeicao_Undefined',
  'Rejeicao_Null',
  'Rejeicao_Function',
  'Rejeicao_Symbol',
  'Rejeicao_Bigint',
] as const;
export type ClasseDeErro = (typeof CLASSES_DE_ERRO)[number];

/** A classe estrutural de um valor lançado/rejeitado. Nunca lê `name` nem `code`. */
export function classeDeErro(valor: unknown): ClasseDeErro {
  if (valor instanceof Error) {
    let proto: object | null = Object.getPrototypeOf(valor) as object | null;
    // Sobe a cadeia até achar uma classe conhecida: `class MeuErro extends TypeError` → TypeError.
    while (proto) {
      const nome = (proto.constructor as { name?: unknown } | undefined)?.name;
      if (typeof nome === 'string' && (CLASSES_DE_ERRO as readonly string[]).includes(nome) && nome !== 'Error') return nome as ClasseDeErro;
      proto = Object.getPrototypeOf(proto) as object | null;
    }
    return 'Error';
  }
  if (valor === null) return 'Rejeicao_Null';
  if (Array.isArray(valor)) return 'Rejeicao_Array';
  const t = typeof valor;
  const nome = `Rejeicao_${t.charAt(0).toUpperCase()}${t.slice(1)}`;
  return (CLASSES_DE_ERRO as readonly string[]).includes(nome) ? (nome as ClasseDeErro) : 'Rejeicao_Object';
}

/** Só aceita um nome que esteja na allowlist; qualquer outro é descartado. */
export function nomeDeErroValido(nome: unknown): ClasseDeErro | undefined {
  return typeof nome === 'string' && (CLASSES_DE_ERRO as readonly string[]).includes(nome) ? (nome as ClasseDeErro) : undefined;
}

/**
 * Só os frames técnicos do stack: a primeira linha (`Error: <mensagem>`) e
 * qualquer linha que não seja frame vão embora — é lá que a mensagem livre
 * mora. Frames V8 (`at f (url:l:c)`) e WebKit/Gecko (`f@url:l:c`) são aceitos;
 * cada um ainda passa por `sanitizarTexto` (URLs perdem query, ids somem).
 */
export function apenasFramesDoStack(stack: unknown, limite: number = LIMITES.stack): string | undefined {
  if (typeof stack !== 'string') return undefined;
  const frames = stack
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => /^at\s+\S/.test(l) || /^[^\s@]*@\S+:\d+/.test(l) || /^[^\s@]*@\[native code\]$/.test(l));
  if (frames.length === 0) return undefined;
  return sanitizarTexto(frames.join('\n'), limite);
}

/* ───────────────────────────── sanitização ───────────────────────────── */

const PADROES_SENSIVEIS: readonly [RegExp, string | ((m: string) => string)][] = [
  // Cabeçalhos/cookies que alguém possa ter interpolado numa mensagem: o
  // resto da linha vai embora (um cookie tem vários pares separados por `;`).
  [/\b(authorization|cookie|set-cookie)\s*[:=]\s*[^\n]+/gi, '$1=[removido]'],
  [/\b(bearer)\s+[a-z0-9._~+/=-]+/gi, '$1 [removido]'],
  // Chaves nomeadas: token=, password=, secret=…
  [/\b(token|password|senha|secret|api[_-]?key|signature|sig|x-amz-[a-z-]+)\s*[=:]\s*[^\s&"']+/gi, '$1=[removido]'],
  // JSON com esses campos.
  [/"(token|password|confirmPassword|newPassword|secret|cookie|authorization)"\s*:\s*"[^"]*"/gi, '"$1":"[removido]"'],
  // URLs perdem query/fragment (URLs assinadas vivem na query). Dentro de um
  // stack a URL vem seguida de `:linha:coluna)` — isso fica: a query é cortada
  // só até o próximo `:`, `)` ou espaço.
  [/https?:\/\/[^\s"'<>]+/gi, (m: string) => m.replace(/[?#][^\s):]*/g, '')],
  // E-mails.
  [/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, '[email]'],
  // Coordenadas: pares decimais com sinal/casas típicas de lat,lng.
  [/-?\d{1,3}\.\d{4,}\s*,\s*-?\d{1,3}\.\d{4,}/g, '[coord]'],
  // UUIDs (ids de viagem, despesa, pessoa…) — o incidente não precisa deles.
  [/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[id]'],
  // Strings longas opacas (chaves de objeto, JWT, hashes).
  [/\b[A-Za-z0-9_-]{40,}\b/g, '[opaco]'],
];

function semQueryEFragment(url: string): string {
  const corte = url.search(/[?#]/);
  return corte === -1 ? url : url.slice(0, corte);
}

/** Apaga padrões sensíveis e corta no limite. Idempotente. */
export function sanitizarTexto(texto: unknown, limite: number): string | undefined {
  if (typeof texto !== 'string') return undefined;
  let t = texto.replace(/\u0000/g, '');
  for (const [re, sub] of PADROES_SENSIVEIS) t = typeof sub === 'string' ? t.replace(re, sub) : t.replace(re, sub);
  t = t.trim();
  if (!t) return undefined;
  return t.length > limite ? `${t.slice(0, limite - 1)}…` : t;
}

/**
 * Rota genérica: sem query, sem fragment, sem ids. `/roteiro/2026-11-04/abc…`
 * vira `/roteiro/:data/:id`. Serve para "em qual tela", nunca para "qual
 * viagem".
 */
export function rotaGenerica(pathname: unknown): string | undefined {
  if (typeof pathname !== 'string') return undefined;
  let p = pathname;
  try {
    if (/^https?:\/\//i.test(p)) p = new URL(p).pathname;
  } catch {
    /* não era URL absoluta válida */
  }
  p = semQueryEFragment(p);
  const partes = p
    .split('/')
    .map((seg) => {
      if (!seg) return seg;
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return ':id';
      if (/^\d{4}-\d{2}-\d{2}$/.test(seg)) return ':data';
      if (/^\d+$/.test(seg)) return ':n';
      if (/^[A-Za-z0-9_-]{16,}$/.test(seg)) return ':id';
      return seg;
    })
    .join('/');
  const limpa = partes || '/';
  return limpa.length > LIMITES.route ? limpa.slice(0, LIMITES.route) : limpa;
}

/* ───────────────────────────── classificação ───────────────────────────── */

export type ClassificacaoHttp = 'incidente' | 'esperado';

/**
 * O que vira relatório. 5xx sempre; falha de rede só COM rede (offline é o
 * produto funcionando — o outbox cuida); qualquer 4xx é regra de domínio
 * (401 da sessão, 403, 404 que esconde recurso, 409 de conflito, 422 de
 * validação, 429 de rate limit) e nunca gera ruído.
 */
export function classificarHttp(status: number | null, online: boolean): ClassificacaoHttp {
  if (status === null) return online ? 'incidente' : 'esperado';
  if (status >= 500) return 'incidente';
  return 'esperado';
}

/* ───────────────────────────── validação (allowlist) ───────────────────────────── */

export type ResultadoValidacao =
  | { ok: true; relatorio: ClientErrorReport }
  | { ok: false; motivo: string };

const RELEASE_RE = /^[A-Za-z0-9._-]{1,64}$/;
const REQUEST_ID_RE = /^[A-Za-z0-9._-]{1,64}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Aceita SÓ o que está na allowlist, com tipo e tamanho certos, e sanitiza o
 * texto de novo. Chave desconhecida recusa o relatório inteiro — é assim que
 * um campo novo nunca entra sem passar por aqui.
 */
export function validarRelatorio(corpo: unknown): ResultadoValidacao {
  if (!corpo || typeof corpo !== 'object' || Array.isArray(corpo)) return { ok: false, motivo: 'corpo inválido' };
  const b = corpo as Record<string, unknown>;

  for (const chave of Object.keys(b)) {
    if (!(CLIENT_ERROR_REPORT_KEYS as readonly string[]).includes(chave)) return { ok: false, motivo: `campo não permitido: ${chave.slice(0, 40)}` };
  }

  if (typeof b['diagnosticCode'] !== 'string' || !DIAGNOSTIC_CODE_RE.test(b['diagnosticCode'])) return { ok: false, motivo: 'diagnosticCode inválido' };
  if (typeof b['occurredAt'] !== 'string' || Number.isNaN(Date.parse(b['occurredAt']))) return { ok: false, motivo: 'occurredAt inválido' };
  if (typeof b['source'] !== 'string' || !(CLIENT_ERROR_SOURCES as readonly string[]).includes(b['source'])) return { ok: false, motivo: 'source inválido' };
  if (typeof b['clientRelease'] !== 'string' || !RELEASE_RE.test(b['clientRelease'])) return { ok: false, motivo: 'clientRelease inválido' };

  const texto = (chave: keyof typeof LIMITES & keyof ClientErrorReport) => {
    const v = b[chave];
    if (v === undefined || v === null) return undefined;
    if (typeof v !== 'string') throw new Error(`${chave} deve ser texto`);
    return sanitizarTexto(v, LIMITES[chave]);
  };
  const booleano = (chave: keyof ClientErrorReport) => {
    const v = b[chave];
    if (v === undefined || v === null) return undefined;
    if (typeof v !== 'boolean') throw new Error(`${chave} deve ser booleano`);
    return v;
  };
  const inteiro = (chave: keyof ClientErrorReport, min: number, max: number) => {
    const v = b[chave];
    if (v === undefined || v === null) return undefined;
    if (typeof v !== 'number' || !Number.isInteger(v) || v < min || v > max) throw new Error(`${chave} fora do intervalo`);
    return v;
  };

  try {
    const requestId = b['requestId'];
    if (requestId !== undefined && requestId !== null && (typeof requestId !== 'string' || !REQUEST_ID_RE.test(requestId))) return { ok: false, motivo: 'requestId inválido' };
    const serverReleaseSeen = b['serverReleaseSeen'];
    if (serverReleaseSeen !== undefined && serverReleaseSeen !== null && (typeof serverReleaseSeen !== 'string' || !RELEASE_RE.test(serverReleaseSeen))) return { ok: false, motivo: 'serverReleaseSeen inválido' };
    const tripId = b['tripId'];
    if (tripId !== undefined && tripId !== null && (typeof tripId !== 'string' || !UUID_RE.test(tripId))) return { ok: false, motivo: 'tripId inválido' };

    const relatorio: ClientErrorReport = {
      diagnosticCode: b['diagnosticCode'],
      occurredAt: new Date(b['occurredAt']).toISOString(),
      source: b['source'] as ClientErrorSource,
      clientRelease: b['clientRelease'],
    };
    const opc = <K extends keyof ClientErrorReport>(k: K, v: ClientErrorReport[K] | undefined) => {
      if (v !== undefined) relatorio[k] = v;
    };
    const source = b['source'] as ClientErrorSource;
    if (b['errorName'] !== undefined && b['errorName'] !== null && typeof b['errorName'] !== 'string') throw new Error('errorName deve ser texto');
    opc('errorName', nomeDeErroValido(b['errorName']));
    // Mensagem livre nunca chega ao banco: fonte não controlada perde a
    // mensagem; fonte controlada só passa com o template nosso.
    if (b['message'] !== undefined && b['message'] !== null && typeof b['message'] !== 'string') throw new Error('message deve ser texto');
    opc('message', mensagemPersistivel(source, b['message']));
    if (b['stack'] !== undefined && b['stack'] !== null && typeof b['stack'] !== 'string') throw new Error('stack deve ser texto');
    opc('stack', apenasFramesDoStack(b['stack']));
    opc('componentStack', texto('componentStack'));
    opc('route', b['route'] === undefined || b['route'] === null ? undefined : rotaGenerica(b['route']));
    opc('requestId', typeof requestId === 'string' ? requestId : undefined);
    opc('httpStatus', inteiro('httpStatus', 100, 599));
    opc('serverReleaseSeen', typeof serverReleaseSeen === 'string' ? serverReleaseSeen : undefined);
    opc('online', booleano('online'));
    opc('standalone', booleano('standalone'));
    opc('serviceWorkerControlled', booleano('serviceWorkerControlled'));
    opc('userAgent', texto('userAgent'));
    opc('viewportWidth', inteiro('viewportWidth', 0, 20000));
    opc('viewportHeight', inteiro('viewportHeight', 0, 20000));
    opc('language', texto('language'));
    opc('tripId', typeof tripId === 'string' ? tripId : undefined);
    return { ok: true, relatorio };
  } catch (erro) {
    return { ok: false, motivo: erro instanceof Error ? erro.message : 'relatório inválido' };
  }
}
